import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import type { AccentColor, Entry, GeoJsonGeometry, Layer } from "shared";
import { resolveLayerColor } from "shared";
import { setShareToken } from "@/api/client";
import { entriesApi } from "@/api/entries";
import { layersApi } from "@/api/layers";
import { mapsApi } from "@/api/maps";
import { useApi } from "@/api/hooks";
import { useConfirm } from "@/components/feedback/Confirm";
import { useToast } from "@/components/feedback/Toast";
import { AppShell } from "@/components/layout/AppShell";
import { buildEntryListItems } from "@/components/map/EntriesList";
import { DrawToolbar, type DrawMode, type ViewportTarget } from "@/components/map/DrawToolbar";
import { EntryEditor } from "@/components/map/EntryEditor";
import { viewportForEntries } from "@/components/map/fitEntries";
import { LayerPanel } from "@/components/map/LayerPanel";
import { LocationSearch } from "@/components/map/LocationSearch";
import { ImportDialog } from "@/components/map/ImportDialog";
import {
	geometryFromDraft,
	MapView,
	type MapLayerData,
	type ViewportCommand,
} from "@/components/map/MapView";
import { ShareDialog } from "@/components/map/ShareDialog";
import { Button } from "@/components/primitives/Button";
import { IconButton } from "@/components/primitives/IconButton";
import { IconChevronLeft, IconShare } from "@/components/primitives/icons";
import { useSession } from "@/session/SessionProvider";

function defaultTitle(mode: DrawMode, count: number): string {
	if (mode === "point") return `Punkt ${count + 1}`;
	if (mode === "line") return `Straße ${count + 1}`;
	if (mode === "polygon") return `Bereich ${count + 1}`;
	return `Eintrag ${count + 1}`;
}

function editedGeometryFromDraft(
	entry: Entry,
	vertices: [number, number][],
): GeoJsonGeometry | null {
	const built = geometryFromDraft(entry.geomType, vertices);
	if (!built) return null;
	if (entry.geomType !== "polygon") return built;
	const oldCoords = entry.geometry.coordinates;
	if (Array.isArray(oldCoords) && oldCoords.length > 1) {
		return {
			type: "Polygon",
			coordinates: [
				(built.coordinates as number[][][])[0],
				...(oldCoords as number[][][]).slice(1),
			],
		};
	}
	return built;
}

export default function MapEditor() {
	const { mapId = "" } = useParams();
	const [searchParams] = useSearchParams();
	const shareToken = searchParams.get("t");
	const navigate = useNavigate();
	const { toast } = useToast();
	const confirm = useConfirm();
	const { isAuthed, user, logout } = useSession();
	const [shareOpen, setShareOpen] = useState(false);
	const [importOpen, setImportOpen] = useState(false);

	useEffect(() => {
		setShareToken(shareToken);
		return () => setShareToken(null);
	}, [shareToken]);

	const { data, loading, error, reload } = useApi(
		() => mapsApi.get(mapId),
		[mapId, shareToken],
		Boolean(mapId),
	);

	const [layers, setLayers] = useState<Layer[]>([]);
	const [entriesByLayer, setEntriesByLayer] = useState<Record<string, Entry[]>>({});
	const [visibility, setVisibility] = useState<Record<string, boolean>>({});
	const [activeLayerId, setActiveLayerId] = useState<string | null>(null);
	const [drawMode, setDrawMode] = useState<DrawMode>("select");
	const [draftVertices, setDraftVertices] = useState<[number, number][]>([]);
	const [selectedEntryId, setSelectedEntryId] = useState<string | null>(null);
	const [editingEntryId, setEditingEntryId] = useState<string | null>(null);
	const [entriesLoading, setEntriesLoading] = useState(false);
	const [addingLayer, setAddingLayer] = useState(false);
	const [deletingLayer, setDeletingLayer] = useState(false);
	const [savingEntry, setSavingEntry] = useState(false);
	const [persisting, setPersisting] = useState(false);
	const [viewportCommand, setViewportCommand] = useState<ViewportCommand | null>(null);
	const viewportKeyRef = useRef(0);
	const fittedMapIdRef = useRef<string | null>(null);

	const draftRef = useRef(draftVertices);
	const drawModeRef = useRef(drawMode);
	const activeLayerIdRef = useRef(activeLayerId);
	const entriesByLayerRef = useRef(entriesByLayer);
	const editingEntryIdRef = useRef(editingEntryId);
	draftRef.current = draftVertices;
	drawModeRef.current = drawMode;
	activeLayerIdRef.current = activeLayerId;
	entriesByLayerRef.current = entriesByLayer;
	editingEntryIdRef.current = editingEntryId;

	useEffect(() => {
		if (!data) return;
		setLayers(data.layers);
		const vis: Record<string, boolean> = {};
		for (const layer of data.layers) {
			vis[layer.id] = layer.defaultVisible;
		}
		setVisibility(vis);
		setActiveLayerId((prev) => {
			if (prev && data.layers.some((l) => l.id === prev)) return prev;
			return data.layers[0]?.id ?? null;
		});
	}, [data]);

	const loadEntries = useCallback(async (layerList: Layer[]) => {
		if (layerList.length === 0) {
			setEntriesByLayer({});
			return;
		}
		setEntriesLoading(true);
		try {
			const pairs = await Promise.all(
				layerList.map(async (layer) => {
					const res = await entriesApi.list(layer.id);
					return [layer.id, res.entries] as const;
				}),
			);
			const next: Record<string, Entry[]> = {};
			for (const [id, entries] of pairs) {
				next[id] = entries;
			}
			setEntriesByLayer(next);
		} catch (e) {
			toast(e instanceof Error ? e.message : "Einträge laden fehlgeschlagen", "error");
		} finally {
			setEntriesLoading(false);
		}
	}, [toast]);

	useEffect(() => {
		if (layers.length === 0) {
			setEntriesByLayer({});
			return;
		}
		void loadEntries(layers);
	}, [layers, loadEntries]);

	useEffect(() => {
		fittedMapIdRef.current = null;
	}, [mapId]);

	useEffect(() => {
		if (entriesLoading || !mapId) return;
		if (fittedMapIdRef.current === mapId) return;
		const target = viewportForEntries(entriesByLayer);
		if (!target) return;
		fittedMapIdRef.current = mapId;
		viewportKeyRef.current += 1;
		setViewportCommand({ ...target, key: viewportKeyRef.current });
	}, [entriesByLayer, entriesLoading, mapId]);

	const selectedEntry = useMemo(() => {
		if (!selectedEntryId) return null;
		for (const entries of Object.values(entriesByLayer)) {
			const found = entries.find((e) => e.id === selectedEntryId);
			if (found) return found;
		}
		return null;
	}, [entriesByLayer, selectedEntryId]);

	const mapLayers: MapLayerData[] = useMemo(
		() =>
			layers.map((layer, index) => ({
				id: layer.id,
				title: layer.title,
				color: resolveLayerColor(layer.style, index),
				visible: visibility[layer.id] ?? layer.defaultVisible,
				entries: entriesByLayer[layer.id] ?? [],
			})),
		[layers, visibility, entriesByLayer],
	);

	const entryItems = useMemo(
		() => buildEntryListItems(layers, entriesByLayer),
		[layers, entriesByLayer],
	);

	const totalEntries = entryItems.length;

	const resetDraft = () => setDraftVertices([]);

	const handleModeChange = (mode: DrawMode) => {
		if (mode === drawModeRef.current) return;
		setDrawMode(mode);
		resetDraft();
		setEditingEntryId(null);
		if (mode !== "select") {
			setSelectedEntryId(null);
		}
	};

	const persistEntry = useCallback(async (
		vertices: [number, number][],
		mode: DrawMode,
	) => {
		const layerId = activeLayerIdRef.current;
		const editId = editingEntryIdRef.current;
		if (mode === "select" || (!editId && !layerId)) {
			toast("Bitte zuerst eine Ebene auswählen", "error");
			return;
		}
		const geometry = geometryFromDraft(mode, vertices);
		if (!geometry) {
			if (mode === "line") {
				toast("Straße braucht mindestens 2 Punkte", "error");
			} else if (mode === "polygon") {
				toast("Bereich braucht mindestens 3 Ecken", "error");
			}
			return;
		}

		if (editId) {
			const existingEntry = Object.values(entriesByLayerRef.current)
				.flat()
				.find((e) => e.id === editId);
			const nextGeometry = existingEntry
				? (editedGeometryFromDraft(existingEntry, vertices) ?? geometry)
				: geometry;
			setPersisting(true);
			try {
				const { entry } = await entriesApi.update(editId, { geometry: nextGeometry });
				setEntriesByLayer((prev) => ({
					...prev,
					[entry.layerId]: (prev[entry.layerId] ?? []).map((e) =>
						e.id === entry.id ? entry : e,
					),
				}));
				setSelectedEntryId(entry.id);
				setEditingEntryId(null);
				setDrawMode("select");
				setDraftVertices([]);
				toast("Geometrie gespeichert", "success");
			} catch (e) {
				toast(e instanceof Error ? e.message : "Speichern fehlgeschlagen", "error");
			} finally {
				setPersisting(false);
			}
			return;
		}

		if (!layerId) return;
		const existing = entriesByLayerRef.current[layerId] ?? [];
		const title = defaultTitle(mode, existing.length);

		setPersisting(true);
		try {
			const { entry } = await entriesApi.create(layerId, {
				geomType: mode,
				geometry,
				title,
			});
			setEntriesByLayer((prev) => ({
				...prev,
				[layerId]: [entry, ...(prev[layerId] ?? [])],
			}));
			setSelectedEntryId(entry.id);
			setDrawMode("select");
			setDraftVertices([]);
			toast("Eintrag gespeichert", "success");
		} catch (e) {
			toast(e instanceof Error ? e.message : "Speichern fehlgeschlagen", "error");
		} finally {
			setPersisting(false);
		}
	}, [toast]);

	const handleMapClick = useCallback((lngLat: [number, number]) => {
		const layerId = activeLayerIdRef.current;
		const mode = drawModeRef.current;
		if (!layerId) {
			toast("Bitte zuerst eine Ebene auswählen", "error");
			return;
		}
		if (mode === "select") return;

		if (mode === "point") {
			void persistEntry([lngLat], "point");
			return;
		}

		setDraftVertices((prev) => [...prev, lngLat]);
	}, [persistEntry, toast]);

	const handleFinishDraft = useCallback(() => {
		const mode = drawModeRef.current;
		const vertices = draftRef.current;
		if (mode !== "line" && mode !== "polygon") return;
		void persistEntry(vertices, mode);
	}, [persistEntry]);

	const handleEditGeometry = (entry: Entry) => {
		if (entry.geomType !== "polygon" && entry.geomType !== "line") return;
		let vertices: [number, number][] = [];
		if (entry.geomType === "polygon") {
			const coords = entry.geometry.coordinates;
			if (!Array.isArray(coords)) return;
			const ring = (coords as [number, number][][])[0] ?? [];
			const closed =
				ring.length > 1 &&
				ring[0][0] === ring[ring.length - 1][0] &&
				ring[0][1] === ring[ring.length - 1][1];
			vertices = closed ? ring.slice(0, -1) : [...ring];
		} else {
			const coords = entry.geometry.coordinates;
			if (!Array.isArray(coords)) return;
			vertices = [...(coords as [number, number][])];
		}
		if (vertices.length < 2) return;
		setSelectedEntryId(entry.id);
		setEditingEntryId(entry.id);
		setDrawMode(entry.geomType);
		setDraftVertices(vertices);
	};

	const handleVertexInsert = (index: number, coord: [number, number]) => {
		setDraftVertices((prev) => {
			const clamped = Math.max(0, Math.min(index, prev.length));
			const next = [...prev];
			next.splice(clamped, 0, coord);
			return next;
		});
	};

	const handleVertexRemove = (index: number) => {
		const mode = drawModeRef.current;
		const min = mode === "polygon" ? 3 : 2;
		if (draftRef.current.length <= min) {
			toast(
				mode === "polygon"
					? "Bereich braucht mindestens 3 Ecken"
					: "Straße braucht mindestens 2 Punkte",
				"error",
			);
			return;
		}
		setDraftVertices((prev) => prev.filter((_, i) => i !== index));
	};

	const handleVertexDrag = (index: number, coord: [number, number]) => {
		setDraftVertices((prev) => {
			if (index < 0 || index >= prev.length) return prev;
			const next = [...prev];
			next[index] = coord;
			return next;
		});
	};

	const handleCancelDraft = () => {
		if (editingEntryIdRef.current) {
			setEditingEntryId(null);
			setDrawMode("select");
		}
		setDraftVertices([]);
	};

	useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			const target = e.target as HTMLElement | null;
			const inField =
				!!target &&
				(target.tagName === "INPUT" ||
					target.tagName === "TEXTAREA" ||
					target.isContentEditable);
			if (
				e.key === "Enter" &&
				!inField &&
				(drawModeRef.current === "line" || drawModeRef.current === "polygon")
			) {
				e.preventDefault();
				handleFinishDraft();
			}
			if (e.key === "Escape") {
				if (editingEntryIdRef.current) {
					setEditingEntryId(null);
					setDraftVertices([]);
					setDrawMode("select");
				} else if (draftRef.current.length > 0) {
					setDraftVertices([]);
				} else if (drawModeRef.current !== "select") {
					setDrawMode("select");
				}
			}
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, [handleFinishDraft]);

	const handleAddLayer = async () => {
		if (!mapId) return;
		setAddingLayer(true);
		try {
			const n = layers.length + 1;
			const { layer } = await layersApi.create(mapId, {
				title: `Ebene ${n}`,
				style: { color: "teal" },
				defaultVisible: true,
				zIndex: n,
			});
			setLayers((prev) => [...prev, layer]);
			setVisibility((prev) => ({ ...prev, [layer.id]: true }));
			setActiveLayerId(layer.id);
			setEntriesByLayer((prev) => ({ ...prev, [layer.id]: [] }));
			toast("Ebene angelegt", "success");
		} catch (e) {
			toast(e instanceof Error ? e.message : "Ebene anlegen fehlgeschlagen", "error");
		} finally {
			setAddingLayer(false);
		}
	};

	const handleDeleteLayer = async (layerId: string) => {
		const layer = layers.find((l) => l.id === layerId);
		if (!layer) return;
		const entryCount = (entriesByLayer[layerId] ?? []).length;
		const extra =
			entryCount > 0
				? `${entryCount} Eintrag${entryCount === 1 ? "" : "e"} werden mitgelöscht.`
				: undefined;
		const ok = await confirm({
			title: `Ebene „${layer.title}" löschen?`,
			description: extra,
			confirmLabel: "Löschen",
			variant: "danger",
		});
		if (!ok) return;

		setDeletingLayer(true);
		try {
			await layersApi.remove(layerId);
			const remaining = layers.filter((l) => l.id !== layerId);
			setLayers(remaining);
			setVisibility((prev) => {
				const next = { ...prev };
				delete next[layerId];
				return next;
			});
			setEntriesByLayer((prev) => {
				const next = { ...prev };
				delete next[layerId];
				return next;
			});
			if (activeLayerId === layerId) {
				setActiveLayerId(remaining[0]?.id ?? null);
				resetDraft();
			}
			if (selectedEntry?.layerId === layerId) {
				setSelectedEntryId(null);
			}
			toast("Ebene gelöscht", "success");
		} catch (e) {
			toast(e instanceof Error ? e.message : "Ebene löschen fehlgeschlagen", "error");
		} finally {
			setDeletingLayer(false);
		}
	};

	const handleGoToLocation = (target: ViewportTarget) => {
		viewportKeyRef.current += 1;
		setViewportCommand({ ...target, key: viewportKeyRef.current });
	};

	const handleColorChange = async (layerId: string, color: AccentColor) => {
		const layer = layers.find((l) => l.id === layerId);
		if (!layer) return;
		const style = { ...(layer.style ?? {}), color };
		setLayers((prev) =>
			prev.map((l) => (l.id === layerId ? { ...l, style } : l)),
		);
		try {
			const { layer: updated } = await layersApi.update(layerId, { style });
			setLayers((prev) => prev.map((l) => (l.id === layerId ? updated : l)));
		} catch (e) {
			toast(e instanceof Error ? e.message : "Stil speichern fehlgeschlagen", "error");
			void reload();
		}
	};

	const handleRenameLayer = async (layerId: string, title: string) => {
		const previous = layers.find((l) => l.id === layerId);
		if (!previous || previous.title === title) return;
		setLayers((prev) =>
			prev.map((l) => (l.id === layerId ? { ...l, title } : l)),
		);
		try {
			const { layer: updated } = await layersApi.update(layerId, { title });
			setLayers((prev) => prev.map((l) => (l.id === layerId ? updated : l)));
			toast("Ebene umbenannt", "success");
		} catch (e) {
			toast(e instanceof Error ? e.message : "Umbenennen fehlgeschlagen", "error");
			void reload();
		}
	};

	const handleSelectEntry = (entryId: string | null, opts?: { fly?: boolean }) => {
		setSelectedEntryId(entryId);
		setDrawMode("select");
		setDraftVertices([]);
		setEditingEntryId(null);
		if (!entryId) return;
		const entry = Object.values(entriesByLayerRef.current)
			.flat()
			.find((e) => e.id === entryId);
		if (!entry) return;
		setActiveLayerId(entry.layerId);
		if (opts?.fly === false) return;

		const [minLng, minLat, maxLng, maxLat] = entry.bbox;
		const center: [number, number] = [
			(minLng + maxLng) / 2,
			(minLat + maxLat) / 2,
		];
		const isPoint =
			entry.geomType === "point" ||
			(minLng === maxLng && minLat === maxLat);
		handleGoToLocation({
			center,
			bbox: isPoint ? null : entry.bbox,
			zoom: isPoint ? 15 : undefined,
		});
	};

	const handleSaveEntry = async (input: { title: string; description: string }) => {
		if (!selectedEntry) return;
		const editingThis = editingEntryIdRef.current === selectedEntry.id;
		let geometry: GeoJsonGeometry | undefined;
		if (editingThis) {
			const built = editedGeometryFromDraft(selectedEntry, draftRef.current);
			if (!built) {
				toast(
					selectedEntry.geomType === "polygon"
						? "Bereich braucht mindestens 3 Ecken"
						: "Straße braucht mindestens 2 Punkte",
					"error",
				);
				return;
			}
			geometry = built;
		}
		setSavingEntry(true);
		try {
			const { entry } = await entriesApi.update(selectedEntry.id, {
				title: input.title,
				description: input.description || undefined,
				...(geometry ? { geometry } : {}),
			});
			setEntriesByLayer((prev) => ({
				...prev,
				[entry.layerId]: (prev[entry.layerId] ?? []).map((e) =>
					e.id === entry.id ? entry : e,
				),
			}));
			if (editingThis) {
				setEditingEntryId(null);
				setDraftVertices([]);
				setDrawMode("select");
			}
			toast("Gespeichert", "success");
		} catch (e) {
			toast(e instanceof Error ? e.message : "Speichern fehlgeschlagen", "error");
		} finally {
			setSavingEntry(false);
		}
	};

	const handleDeleteEntry = async () => {
		if (!selectedEntry) return;
		const ok = await confirm({
			title: `„${selectedEntry.title}" wirklich löschen?`,
			confirmLabel: "Löschen",
			variant: "danger",
		});
		if (!ok) return;
		setSavingEntry(true);
		try {
			await entriesApi.remove(selectedEntry.id);
			const layerId = selectedEntry.layerId;
			setEntriesByLayer((prev) => ({
				...prev,
				[layerId]: (prev[layerId] ?? []).filter((e) => e.id !== selectedEntry.id),
			}));
			setSelectedEntryId(null);
			setEditingEntryId(null);
			setDraftVertices([]);
			toast("Eintrag gelöscht", "success");
		} catch (e) {
			toast(e instanceof Error ? e.message : "Löschen fehlgeschlagen", "error");
		} finally {
			setSavingEntry(false);
		}
	};

	const handleMoveEntry = async (target: {
		mapId: string;
		layerId: string;
		layerTitle: string;
	}) => {
		if (!selectedEntry) return;
		const sourceLayerId = selectedEntry.layerId;
		if (target.layerId === sourceLayerId) return;

		setEditingEntryId(null);
		setDraftVertices([]);
		setSavingEntry(true);
		try {
			const { entry } = await entriesApi.update(selectedEntry.id, {
				layerId: target.layerId,
			});

			if (target.mapId !== mapId) {
				setEntriesByLayer((prev) => ({
					...prev,
					[sourceLayerId]: (prev[sourceLayerId] ?? []).filter(
						(e) => e.id !== selectedEntry.id,
					),
				}));
				setSelectedEntryId(null);
				toast(`Nach „${target.layerTitle}" auf anderer Karte verschoben`, "success");
				return;
			}

			setEntriesByLayer((prev) => {
				const without = {
					...prev,
					[sourceLayerId]: (prev[sourceLayerId] ?? []).filter(
						(e) => e.id !== selectedEntry.id,
					),
				};
				return {
					...without,
					[target.layerId]: [entry, ...(without[target.layerId] ?? [])],
				};
			});
			setActiveLayerId(target.layerId);
			toast(`Nach „${target.layerTitle}" verschoben`, "success");
		} catch (e) {
			toast(e instanceof Error ? e.message : "Verschieben fehlgeschlagen", "error");
			throw e;
		} finally {
			setSavingEntry(false);
		}
	};

	if (loading) {
		return (
			<div className="flex min-h-svh items-center justify-center text-sm text-fg-muted">
				Karte laden…
			</div>
		);
	}

	if (error || !data) {
		return (
			<div className="mx-auto flex min-h-svh max-w-lg flex-col items-center justify-center px-4 text-center">
				<p className="text-rose-500">{error?.message ?? "Karte nicht gefunden"}</p>
				<Button as="a" href="/app" variant="secondary" className="mt-6">
					Zurück
				</Button>
			</div>
		);
	}

	const { map, access } = data;
	const center = map.viewState?.center ?? [10.45, 51.16];
	const zoom = map.viewState?.zoom ?? 5.5;
	const canEdit = access.role === "owner" || access.role === "editor";
	const canShare = access.role === "owner" && isAuthed;
	const isViewerOnly = !canEdit;

	if (isViewerOnly) {
		const viewPath = shareToken
			? `/k/${mapId}/view?t=${encodeURIComponent(shareToken)}`
			: `/k/${mapId}/view`;
		return <Navigate to={viewPath} replace />;
	}

	return (
		<AppShell
			mode="map"
			mapBar={
				<header className="absolute inset-x-0 top-0 z-30 flex h-12 items-center gap-2 border-b border-zinc-950/5 bg-surface/85 px-2 backdrop-blur-md dark:border-white/5 sm:px-3">
					<IconButton
						label="Zurück"
						onClick={() => navigate(isAuthed ? "/app" : "/")}
					>
						<IconChevronLeft />
					</IconButton>
					<div className="min-w-0 shrink">
						<p className="truncate text-sm font-medium text-fg">{map.title}</p>
						<p className="truncate text-xs text-fg-muted">
							{layers.length} Ebene{layers.length === 1 ? "" : "n"} · {totalEntries}{" "}
							Eintrag
							{totalEntries === 1 ? "" : "e"}
							{entriesLoading ? " · laden…" : ""}
							{persisting ? " · speichern…" : ""}
							{access.role === "editor" ? " · Editor" : ""}
						</p>
					</div>
					<div className="mx-auto hidden min-w-0 max-w-sm flex-1 md:block">
						<LocationSearch onGoToLocation={handleGoToLocation} />
					</div>
					<div className="ml-auto flex shrink-0 items-center gap-1">
						{canShare ? (
							<Button
								variant="secondary"
								size="sm"
								onClick={() => setShareOpen(true)}
							>
								<IconShare />
								<span className="hidden sm:inline">Teilen</span>
							</Button>
						) : null}
						{isAuthed ? (
							<>
								<span className="hidden max-w-[10rem] truncate text-xs text-fg-muted lg:inline">
									{user?.email}
								</span>
								<Button variant="ghost" size="sm" onClick={() => void logout()}>
									Abmelden
								</Button>
							</>
						) : null}
					</div>
				</header>
			}
		>
			<div className="relative h-svh w-full overflow-hidden">
				<MapView
					center={center}
					zoom={zoom}
					className="absolute inset-0 h-full w-full"
					layers={mapLayers}
					drawMode={drawMode}
					selectedEntryId={selectedEntryId}
					draftVertices={draftVertices}
					editing={Boolean(editingEntryId)}
					viewportCommand={viewportCommand}
					onSelectEntry={(id) => handleSelectEntry(id, { fly: false })}
					onMapClick={handleMapClick}
					onFinishDraft={handleFinishDraft}
					onVertexInsert={handleVertexInsert}
					onVertexRemove={handleVertexRemove}
					onVertexDrag={handleVertexDrag}
				/>

				<div className="absolute inset-x-3 top-14 z-20 md:hidden">
					<LocationSearch onGoToLocation={handleGoToLocation} />
				</div>

				<div className="absolute inset-x-0 bottom-0 z-20 max-md:max-h-[45vh] md:inset-x-auto md:bottom-auto md:left-3 md:top-14 md:w-80">
					<LayerPanel
						layers={layers}
						activeLayerId={activeLayerId}
						visibility={visibility}
						onSelect={(id) => {
							setActiveLayerId(id);
							resetDraft();
							setEditingEntryId(null);
						}}
						onToggleVisibility={(id) => {
							setVisibility((prev) => ({
								...prev,
								[id]: !(prev[id] ?? true),
							}));
						}}
						onColorChange={handleColorChange}
						onRename={(id, title) => void handleRenameLayer(id, title)}
						onDelete={(id) => void handleDeleteLayer(id)}
						onAdd={() => void handleAddLayer()}
						onImport={() => setImportOpen(true)}
						adding={addingLayer}
						deleting={deletingLayer}
						canDeleteLayers={access.role === "owner"}
						entryItems={entryItems}
						selectedEntryId={selectedEntryId}
						onSelectEntry={handleSelectEntry}
						entriesLoading={entriesLoading}
						className="max-md:max-w-none"
					/>
				</div>

				<div className="pointer-events-none absolute inset-x-0 bottom-6 z-20 flex justify-center px-3 max-md:bottom-[min(48vh,18rem)] md:bottom-8">
					<DrawToolbar
						mode={drawMode}
						onChange={handleModeChange}
						disabled={!activeLayerId || persisting}
						draftCount={draftVertices.length}
						editing={Boolean(editingEntryId)}
						onFinishDraft={handleFinishDraft}
						onCancelDraft={handleCancelDraft}
					/>
				</div>

				{selectedEntry ? (
					<div className="absolute bottom-0 right-0 top-12 z-20 w-full p-0 md:bottom-auto md:right-3 md:top-14 md:w-80 md:p-0">
						<EntryEditor
							entry={selectedEntry}
							currentMapId={mapId}
							layers={layers}
							saving={savingEntry}
							onSave={handleSaveEntry}
							onMove={handleMoveEntry}
							onDelete={handleDeleteEntry}
							onEditGeometry={handleEditGeometry}
							onClose={() => setSelectedEntryId(null)}
						/>
					</div>
				) : null}

				{!activeLayerId ? (
					<div className="pointer-events-none absolute inset-x-0 bottom-24 z-20 flex justify-center px-3 md:bottom-20">
						<p className="rounded-[var(--radius-control)] bg-surface px-3 py-1.5 text-sm text-fg-muted shadow-[var(--shadow-float)] ring-1 ring-[var(--ring-hairline)]">
							Lege eine Ebene an, um zu zeichnen.
						</p>
					</div>
				) : null}

				{canShare ? (
					<ShareDialog
						mapId={mapId}
						open={shareOpen}
						onClose={() => setShareOpen(false)}
					/>
				) : null}

				<ImportDialog
					mapId={mapId}
					open={importOpen}
					onClose={() => setImportOpen(false)}
					onImported={async () => {
						fittedMapIdRef.current = null;
						await reload();
					}}
				/>
			</div>
		</AppShell>
	);
}
